import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Form, FormField, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { trackOutboundLink } from '@/lib/analytics';
import { Check, ArrowRight, Mail, Phone, Building, User, ChevronDown, BriefcaseBusiness, MapPin } from 'lucide-react';
import { FORM_PROXY_URL } from '@/config/api';

// Cloudflare Turnstile site key (public — safe to expose in the bundle)
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

// Minimal typing for the Turnstile script loaded in index.html
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

import { SEO } from '@/components/common/seo';

const BookADemo = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    company: '',
    designation: '',
    location: '',
    message: '',
  });
  const [showCountryCodes, setShowCountryCodes] = useState(false);
  
  // Common country codes
  const countryCodes = [
    { code: '+1', country: 'US/Canada' },
    { code: '+44', country: 'UK' },
    { code: '+91', country: 'India' },
    { code: '+61', country: 'Australia' },
    { code: '+49', country: 'Germany' },
    { code: '+33', country: 'France' },
    { code: '+81', country: 'Japan' },
    { code: '+86', country: 'China' },
    { code: '+65', country: 'Singapore' },
    { code: '+971', country: 'UAE' },
  ];
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Anti-spam: honeypot field (bots fill it, humans never see it)
  const [honeypot, setHoneypot] = useState('');
  // Anti-spam: Cloudflare Turnstile token (set once the challenge is solved)
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Render the Turnstile widget once its script is ready
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;

    const tryRender = () => {
      if (cancelled || widgetIdRef.current !== null) return true;
      if (!window.turnstile || !turnstileRef.current) return false;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => {
          setCaptchaToken(token);
          setErrors(prev => ({ ...prev, captcha: '' }));
        },
        'expired-callback': () => setCaptchaToken(''),
        'error-callback': () => setCaptchaToken(''),
      });
      return true;
    };

    let interval: number | undefined;
    if (!tryRender()) {
      interval = window.setInterval(() => {
        if (tryRender()) window.clearInterval(interval);
      }, 200);
    }

    return () => {
      cancelled = true;
      if (interval) window.clearInterval(interval);
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formState.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formState.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formState.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formState.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formState.designation.trim()) {
      newErrors.designation = 'Designation is required';
    }
    
    if (!formState.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (!formState.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formState.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot: if this hidden field is filled, it's a bot.
    // Show the success state so the bot thinks it worked, but send nothing.
    if (honeypot) {
      setIsSubmitted(true);
      return;
    }

    if (!validateForm()) {
      return;
    }

    // Require the Turnstile challenge to be solved (only when configured)
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setErrors(prev => ({ ...prev, captcha: 'Please complete the verification.' }));
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Send to the Cloudflare Worker proxy (it holds the NocoDB token and
      // verifies the honeypot + Turnstile server-side before writing).
      const payload = {
        name: formState.name,
        email: formState.email,
        mobile_number: `${formState.countryCode}${formState.phone}`,
        company_name: formState.company,
        location: formState.location,
        message: formState.message || 'No message provided',
        designation: formState.designation,
        company_website: honeypot, // honeypot — should be empty for real users
        turnstileToken: captchaToken,
      };

      const response = await fetch(FORM_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      console.log('Form submitted successfully');
      trackOutboundLink('demo_request');
      setIsSubmitted(true);
      
      // Reset form after submission
      setFormState({
        name: '',
        email: '',
        countryCode: '+91',
        phone: '',
        company: '',
        designation: '',
        location: '',
        message: '',
      });

      // Reset the Turnstile challenge for the next submission
      setCaptchaToken('');
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-32 md:py-0 px-4">
      <SEO canonical="/book-a-demo" />
      
      <div className="max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left column - Hero content */}
          <motion.div 
            className="flex flex-col justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Experience the future of <span className="text-primary">radiology</span>
            </h1>
            
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Schedule a personalized demo with our team and discover how Somatiq can transform your radiology workflow, improve efficiency, and enhance patient care.
            </p>
            
            <div className="space-y-6 mt-4">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-2 rounded-full">
                  <Check className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Personalized Demonstration</h3>
                  <p className="text-sm text-muted-foreground">Tailored to your specific needs and workflow</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-2 rounded-full">
                  <Check className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Expert Consultation</h3>
                  <p className="text-sm text-muted-foreground">Speak directly with our radiology specialists</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-2 rounded-full">
                  <Check className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Custom Implementation Plan</h3>
                  <p className="text-sm text-muted-foreground">Get a roadmap for seamless integration</p>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Right column - Form */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-3xl blur-3xl -z-10" />
            
            <div className="bg-background/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8 shadow-xl">
              {isSubmitted ? (
                <motion.div 
                  className="text-center py-12"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                    <Check className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold mb-4">Thank You!</h2>
                  <p className="text-muted-foreground mb-6">
                    We've received your request and will contact you shortly to schedule your personalized demo.
                  </p>
                  <Button 
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="cursor-pointer"
                  >
                    Request Another Demo
                  </Button>
                </motion.div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold mb-2">Book Your Demo</h2>
                  <p className="text-muted-foreground mb-6">
                    Fill out the form below and our team will get back to you within 24 hours.
                  </p>
                  
                  <Form onSubmit={handleSubmit} className="space-y-4">
                    {/* Honeypot: hidden anti-spam field. Real users never see or fill it. */}
                    <div
                      aria-hidden="true"
                      style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}
                    >
                      <label htmlFor="company_website">Company website</label>
                      <input
                        type="text"
                        id="company_website"
                        name="company_website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                      />
                    </div>

                    <FormField className="space-y-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <FormLabel>Full Name</FormLabel>
                      </div>
                      <div>
                        <Input
                          name="name"
                          value={formState.name}
                          onChange={handleChange}
                          placeholder="Your Full Name"
                          className={cn("placeholder:text-muted-foreground/50", errors.name && "border-destructive")}
                        />
                        {errors.name && <FormMessage>{errors.name}</FormMessage>}
                      </div>
                    </FormField>
                    
                    <FormField className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <FormLabel>Email Address</FormLabel>
                      </div>
                      <div>
                        <Input
                          name="email"
                          type="email"
                          value={formState.email}
                          onChange={handleChange}
                          placeholder="Enter Email Address"
                          className={cn("placeholder:text-muted-foreground/50", errors.email && "border-destructive")}
                        />
                        {errors.email && <FormMessage>{errors.email}</FormMessage>}
                      </div>
                    </FormField>
                    
                    <FormField className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <FormLabel>Phone Number</FormLabel>
                      </div>
                      <div className="flex gap-2">
                        {/* Country code dropdown */}
                        <div className="relative w-24">
                          <button
                            type="button"
                            className="flex items-center justify-between w-full h-10 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            onClick={() => setShowCountryCodes(!showCountryCodes)}
                          >
                            {formState.countryCode}
                            <ChevronDown className="w-4 h-4 ml-1 text-muted-foreground" />
                          </button>
                          
                          {showCountryCodes && (
                            <div className="absolute z-10 w-48 mt-1 bg-background border border-input rounded-md shadow-lg max-h-60 overflow-auto">
                              {countryCodes.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  className="flex items-center w-full px-3 py-2 text-sm hover:bg-accent text-left"
                                  onClick={() => {
                                    setFormState(prev => ({ ...prev, countryCode: country.code }));
                                    setShowCountryCodes(false);
                                  }}
                                >
                                  <span>{country.code}</span>
                                  <span className="ml-2 text-muted-foreground text-xs">{country.country}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Phone number input */}
                        <Input
                          name="phone"
                          value={formState.phone}
                          onChange={handleChange}
                          placeholder="9876543210"
                          className={cn("flex-1 placeholder:text-muted-foreground/50", errors.phone && "border-destructive")}
                        />
                      </div>
                      {errors.phone && <FormMessage>{errors.phone}</FormMessage>}
                    </FormField>
                    
                    <FormField className="space-y-2">
                      <div className="flex items-center gap-2">
                        <BriefcaseBusiness className="w-4 h-4 text-muted-foreground" />
                        <FormLabel>Designation</FormLabel>
                      </div>
                      <div>
                        <Input
                          name="designation"
                          value={formState.designation}
                          onChange={handleChange}
                          placeholder="Radiology Department Head"
                          className={cn("placeholder:text-muted-foreground/50", errors.designation && "border-destructive")}
                        />
                        {errors.designation && <FormMessage>{errors.designation}</FormMessage>}
                      </div>
                    </FormField>
                    
                    <FormField className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-muted-foreground" />
                        <FormLabel>Organization Name</FormLabel>
                      </div>
                      <div>
                        <Input
                          name="company"
                          value={formState.company}
                          onChange={handleChange}
                          placeholder="Enter Organization Name"
                          className={cn("placeholder:text-muted-foreground/50", errors.company && "border-destructive")}
                        />
                        {errors.company && <FormMessage>{errors.company}</FormMessage>}
                      </div>
                    </FormField>

                    <FormField className="space-y-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <FormLabel>Location</FormLabel>
                      </div>
                      <div>
                        <Input
                          name="location"
                          value={formState.location}
                          onChange={handleChange}
                          placeholder="City, Country"
                          className={cn("placeholder:text-muted-foreground/50", errors.location && "border-destructive")}
                        />
                        {errors.location && <FormMessage>{errors.location}</FormMessage>}
                      </div>
                    </FormField>

                    <FormField className="space-y-2">
                      <div>
                      <FormLabel>How can we help you?</FormLabel>
                      </div>
                      <Textarea
                        name="message"
                        value={formState.message}
                        onChange={handleChange}
                        placeholder="Tell us about your specific needs or questions..."
                        className={cn("placeholder:text-muted-foreground/50", errors.message && "border-destructive")}
                        rows={4}
                      />
                      {errors.message && <FormMessage>{errors.message}</FormMessage>}
                    </FormField>
                    
                    {/* Cloudflare Turnstile anti-spam challenge */}
                    {TURNSTILE_SITE_KEY && (
                      <div className="pt-1">
                        <div ref={turnstileRef} />
                        {errors.captcha && <FormMessage>{errors.captcha}</FormMessage>}
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full mt-2 cursor-pointer"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2 text-foreground">
                          Request Demo <ArrowRight className="w-4 h-4" />
                        </span>
                      )}
                    </Button>
                  </Form>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BookADemo;
